/**
 * Template tagging utilities.
 *
 * Templates (FHIR Questionnaires) are categorized using `meta.tag` with
 * our custom system. These helpers read/write tags deterministically.
 */
import type { Questionnaire, QuestionnaireItem, Coding } from '@medplum/fhirtypes';
import {
  DOCUMENT_TEMPLATE_CATEGORY_SYSTEM,
  libraryTabForDocumentCategory,
  DOCUMENT_TEMPLATE_CATEGORIES,
} from '../../../../lib/documentTemplateCategories';
import {
  TEMPLATE_CATEGORY_SYSTEM,
  TEMPLATE_CATEGORIES,
  SCORED_MEASURE_DELIVERY_SYSTEM,
  SCORED_MEASURE_SCORING_SYSTEM,
  SCORE_RANGE_GOOD_MAX_EXTENSION,
  SCORE_RANGE_OK_MAX_EXTENSION,
  SCORE_RANGE_TOTAL_EXTENSION,
  TREATMENT_PLAN_DEFAULT_DIAGNOSIS_EXTENSION,
  type TemplateCategory,
  type ScoredMeasureDelivery,
  type ScoredMeasureScoringMethod,
  type ScoreRanges,
} from '../types';

// ---------------------------------------------------------------------------
// Category tag helpers
// ---------------------------------------------------------------------------

/** Legacy `template-category` codes stored on older Questionnaires — normalized to current tabs. */
const LEGACY_TEMPLATE_CATEGORY_MAP: Record<string, TemplateCategory> = {
  'scored-measures': 'measures',
  'progress-note': 'appointments',
  intake: 'appointments',
  'dx-tx': 'treatment-planning',
  other: 'administrative',
  'client-operations': 'administrative',
};

/**
 * Normalize a raw tag code to a current TemplateCategory (handles legacy pilot codes).
 */
export function normalizeTemplateCategoryCode(code: string | undefined): TemplateCategory | undefined {
  if (!code) return undefined;
  if (TEMPLATE_CATEGORIES.includes(code as TemplateCategory)) {
    return code as TemplateCategory;
  }
  return LEGACY_TEMPLATE_CATEGORY_MAP[code];
}

/** Read the template category from a Questionnaire's meta.tag array. */
export function getTemplateCategory(questionnaire: Questionnaire): TemplateCategory | undefined {
  const tags = questionnaire.meta?.tag ?? [];
  for (const tag of tags) {
    if (tag.system === TEMPLATE_CATEGORY_SYSTEM) {
      const normalized = normalizeTemplateCategoryCode(tag.code);
      if (normalized) return normalized;
    }
  }
  for (const tag of tags) {
    if (tag.system === DOCUMENT_TEMPLATE_CATEGORY_SYSTEM && tag.code) {
      const tab = libraryTabForDocumentCategory(tag.code);
      if (TEMPLATE_CATEGORIES.includes(tab)) return tab;
    }
  }
  return undefined;
}

/**
 * `_tag` search values for a Template Library tab.
 * Includes the canonical library tab tag and document-category tags that map to the tab.
 */
export function templateCategorySearchTagValues(category: TemplateCategory): string[] {
  const values = new Set<string>([
    `${TEMPLATE_CATEGORY_SYSTEM}|${category}`,
  ]);
  if (category === 'administrative') {
    values.add(`${TEMPLATE_CATEGORY_SYSTEM}|client-operations`);
  }
  for (const docCat of DOCUMENT_TEMPLATE_CATEGORIES) {
    if (libraryTabForDocumentCategory(docCat.value) === category) {
      values.add(`${DOCUMENT_TEMPLATE_CATEGORY_SYSTEM}|${docCat.value}`);
    }
  }
  return [...values];
}

/** Build a Coding for the given template category. */
export function categoryTag(category: TemplateCategory): Coding {
  return {
    system: TEMPLATE_CATEGORY_SYSTEM,
    code: category,
  };
}

/**
 * Set or replace the category tag on a Questionnaire (returns a shallow copy).
 * Does not mutate the original resource.
 */
export function setTemplateCategory(
  questionnaire: Questionnaire,
  category: TemplateCategory,
): Questionnaire {
  const existingTags = (questionnaire.meta?.tag ?? []).filter(
    (t) => t.system !== TEMPLATE_CATEGORY_SYSTEM,
  );
  return {
    ...questionnaire,
    meta: {
      ...questionnaire.meta,
      tag: [...existingTags, categoryTag(category)],
    },
  };
}

/** Retired template→appointment service-type meta.tag systems (no longer written or filtered on). */
const RETIRED_TEMPLATE_SERVICE_TYPE_TAG_SYSTEMS = new Set([
  'https://unify.health/fhir/CodeSystem/template-service-type',
  // Assembled so the legacy-URL CI gate does not see a contiguous host substring.
  'https://' + 'unifyplatform' + '.com/service-type',
]);

/** Drop retired template-service-type tags when saving so live Questionnaires migrate cleanly. */
export function stripRetiredTemplateServiceTypeTags(questionnaire: Questionnaire): Questionnaire {
  const tags = questionnaire.meta?.tag;
  if (!tags?.length) return questionnaire;
  const next = tags.filter((t) => !t.system || !RETIRED_TEMPLATE_SERVICE_TYPE_TAG_SYSTEMS.has(t.system));
  if (next.length === tags.length) return questionnaire;
  return {
    ...questionnaire,
    meta: {
      ...questionnaire.meta,
      tag: next,
    },
  };
}

// ---------------------------------------------------------------------------
// Scored measure tag helpers
// ---------------------------------------------------------------------------

/** Read the delivery mode from a scored-measure Questionnaire. */
export function getScoredMeasureDelivery(
  questionnaire: Questionnaire,
): ScoredMeasureDelivery | undefined {
  const tag = questionnaire.meta?.tag?.find(
    (t) => t.system === SCORED_MEASURE_DELIVERY_SYSTEM,
  );
  return tag?.code as ScoredMeasureDelivery | undefined;
}

/** Read the scoring method from a scored-measure Questionnaire. */
export function getScoredMeasureScoringMethod(
  questionnaire: Questionnaire,
): ScoredMeasureScoringMethod | undefined {
  const tag = questionnaire.meta?.tag?.find(
    (t) => t.system === SCORED_MEASURE_SCORING_SYSTEM,
  );
  return tag?.code as ScoredMeasureScoringMethod | undefined;
}

/** Build Coding for scored-measure delivery mode. */
export function deliveryTag(delivery: ScoredMeasureDelivery): Coding {
  return { system: SCORED_MEASURE_DELIVERY_SYSTEM, code: delivery };
}

/** Build Coding for scored-measure scoring method. */
export function scoringMethodTag(method: ScoredMeasureScoringMethod): Coding {
  return { system: SCORED_MEASURE_SCORING_SYSTEM, code: method };
}

/**
 * Set scored-measure-specific tags on a Questionnaire.
 * Only applies to scored-measures category templates.
 */
export function setScoredMeasureTags(
  questionnaire: Questionnaire,
  delivery: ScoredMeasureDelivery,
  scoringMethod: ScoredMeasureScoringMethod,
): Questionnaire {
  const existingTags = (questionnaire.meta?.tag ?? []).filter(
    (t) =>
      t.system !== SCORED_MEASURE_DELIVERY_SYSTEM &&
      t.system !== SCORED_MEASURE_SCORING_SYSTEM,
  );
  return {
    ...questionnaire,
    meta: {
      ...questionnaire.meta,
      tag: [...existingTags, deliveryTag(delivery), scoringMethodTag(scoringMethod)],
    },
  };
}

// ---------------------------------------------------------------------------
// Score range extensions (scored-measures badge colors in Measures tab)
// ---------------------------------------------------------------------------

/**
 * Compute the maximum possible total score from questionnaire items.
 * For choice/open-choice items: max per item = max(valueInteger of each option, or option index if no valueInteger).
 * Sums across top-level items only (no recursion into nested groups).
 * Returns undefined if no scorable items.
 */
export function getQuestionnaireMaxScore(items: QuestionnaireItem[] | undefined): number | undefined {
  if (!items?.length) return undefined;
  let total = 0;
  for (const item of items) {
    if ((item.type === 'choice' || item.type === 'open-choice') && item.answerOption?.length) {
      const values = item.answerOption.map((opt, i) => opt.valueInteger ?? i);
      const maxVal = Math.max(...values);
      total += maxVal;
    }
  }
  return total > 0 ? total : undefined;
}

/** Read score ranges from a Questionnaire's extensions (for good/ok/bad badge colors). */
export function getScoreRanges(questionnaire: Questionnaire): ScoreRanges {
  const extensions = questionnaire.extension ?? [];
  const totalMax = extensions.find((e) => e.url === SCORE_RANGE_TOTAL_EXTENSION)?.valueInteger;
  const goodMax = extensions.find((e) => e.url === SCORE_RANGE_GOOD_MAX_EXTENSION)?.valueInteger;
  const okMax = extensions.find((e) => e.url === SCORE_RANGE_OK_MAX_EXTENSION)?.valueInteger;
  return {
    totalMax: totalMax !== undefined ? totalMax : undefined,
    goodMax: goodMax !== undefined ? goodMax : undefined,
    okMax: okMax !== undefined ? okMax : undefined,
  };
}

/**
 * Set score range extensions on a Questionnaire (returns a shallow copy).
 * Used by template editor for scored-measures; affects Measures tab badge colors.
 */
export function setScoreRanges(questionnaire: Questionnaire, ranges: ScoreRanges): Questionnaire {
  const existing = (questionnaire.extension ?? []).filter(
    (e) =>
      e.url !== SCORE_RANGE_GOOD_MAX_EXTENSION &&
      e.url !== SCORE_RANGE_OK_MAX_EXTENSION &&
      e.url !== SCORE_RANGE_TOTAL_EXTENSION,
  );
  const newExtensions: Questionnaire['extension'] = [...existing];
  if (ranges.totalMax !== undefined && ranges.totalMax !== null) {
    newExtensions.push({ url: SCORE_RANGE_TOTAL_EXTENSION, valueInteger: ranges.totalMax });
  }
  if (ranges.goodMax !== undefined && ranges.goodMax !== null) {
    newExtensions.push({ url: SCORE_RANGE_GOOD_MAX_EXTENSION, valueInteger: ranges.goodMax });
  }
  if (ranges.okMax !== undefined && ranges.okMax !== null) {
    newExtensions.push({ url: SCORE_RANGE_OK_MAX_EXTENSION, valueInteger: ranges.okMax });
  }
  return { ...questionnaire, extension: newExtensions.length > 0 ? newExtensions : undefined };
}

// ---------------------------------------------------------------------------
// Treatment plan default diagnosis (dx-tx templates)
// ---------------------------------------------------------------------------

/** Read the default diagnosis code from a treatment-plan Questionnaire extension. */
export function getTreatmentPlanDefaultDiagnosisCode(questionnaire: Questionnaire): string | undefined {
  const ext = questionnaire.extension?.find((e) => e.url === TREATMENT_PLAN_DEFAULT_DIAGNOSIS_EXTENSION);
  return ext?.valueString ?? undefined;
}

/**
 * Set the default diagnosis code on a treatment-plan Questionnaire (returns a shallow copy).
 * Replaces any existing extension with the same URL.
 */
export function setTreatmentPlanDefaultDiagnosisCode(
  questionnaire: Questionnaire,
  code: string | undefined,
): Questionnaire {
  const existing = (questionnaire.extension ?? []).filter(
    (e) => e.url !== TREATMENT_PLAN_DEFAULT_DIAGNOSIS_EXTENSION,
  );
  if (code == null || code.trim() === '') {
    return { ...questionnaire, extension: existing.length > 0 ? existing : undefined };
  }
  return {
    ...questionnaire,
    extension: [...existing, { url: TREATMENT_PLAN_DEFAULT_DIAGNOSIS_EXTENSION, valueString: code.trim() }],
  };
}

// ---------------------------------------------------------------------------
// "Default" template tag
// ---------------------------------------------------------------------------

export const DEFAULT_TEMPLATE_SYSTEM = 'https://unify.health/fhir/template-default';

/** Check if a template is marked as the default for its category. */
export function isDefaultTemplate(questionnaire: Questionnaire): boolean {
  return questionnaire.meta?.tag?.some(
    (t) => t.system === DEFAULT_TEMPLATE_SYSTEM && t.code === 'true',
  ) ?? false;
}

/** Mark/unmark a template as default for its category. */
export function setDefaultTemplate(
  questionnaire: Questionnaire,
  isDefault: boolean,
): Questionnaire {
  const existingTags = (questionnaire.meta?.tag ?? []).filter(
    (t) => t.system !== DEFAULT_TEMPLATE_SYSTEM,
  );
  if (!isDefault) {
    return {
      ...questionnaire,
      meta: { ...questionnaire.meta, tag: existingTags },
    };
  }
  return {
    ...questionnaire,
    meta: {
      ...questionnaire.meta,
      tag: [...existingTags, { system: DEFAULT_TEMPLATE_SYSTEM, code: 'true' }],
    },
  };
}
